import React, { useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../../CustomDataGrid'
import { useModal } from '../../../../../store/ModalProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { DateTimeField } from '@mui/x-date-pickers/DateTimeField'
import { usePickersContext } from '@mui/x-date-pickers/hooks'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type {
	DriversVehiclesAvailabilityDriverRow,
	DriversVehiclesAvailabilityModalData,
	DriversVehiclesAvailabilityVehicleRow,
} from '../../../../../types/modal'

const ClickableDateTimeField = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof DateTimeField>>(
	function ClickableDateTimeField(props, ref) {
		const { onOpen } = usePickersContext()

		return (
			<DateTimeField
				{...props}
				ref={ref}
				onClick={event => {
					props.onClick?.(event)
					onOpen(event)
				}}
				style={{ cursor: 'pointer' }}
			/>
		)
	},
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiInputLabel-root.Mui-focused': {
		color: '#092056',
	},
	'& .MuiOutlinedInput-root': {
		fontFamily: 'Poppins, sans-serif',
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		fontFamily: 'Poppins, sans-serif',
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(0, 165, 255, 0.25)',
	borderRadius: '6px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(0, 165, 255, 0.2)',
		},
		'&.Mui-selected': {
			backgroundColor: 'rgba(0, 165, 255, 0.12)',
			'&:hover': {
				backgroundColor: 'rgba(0, 165, 255, 0.12)',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
		transition: 'height 0.2s ease-in-out, padding 0.2s ease-in-out',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
		transition: 'height 0.2s ease-in-out',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .row--highlight': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
	'& .actions-cell': {
		opacity: 0,
		transition: 'opacity 0.2s ease',
		display: 'flex',
		justifyContent: 'flex-end',
		width: '100%',
	},
	'& .MuiDataGrid-row:hover .actions-cell': {
		opacity: 1,
	},
	'& .MuiDataGrid-virtualScroller': {
		transition: 'height 0.2s ease-in-out',
	},
	'& .MuiDataGrid-main': {
		transition: 'height 0.2s ease-in-out',
	},
}

const defaultDrivers: DriversVehiclesAvailabilityDriverRow[] = [
	{
		id: 'driver-1',
		motorista: 'Beatriz Silva',
		numMec: '298',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numFrota: '0062',
		telefone: '910000000',
		inicioPosterior: '02/05/2024 17:00',
		fim: '',
	},
	{
		id: 'driver-2',
		motorista: 'Carlos Cardoso',
		numMec: '123',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numFrota: '0062',
		telefone: '910000000',
		inicioPosterior: '02/05/2024 17:00',
		fim: '',
	},
	{
		id: 'driver-3',
		motorista: 'Rui Henriques',
		numMec: '098',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numFrota: '0062',
		telefone: '910000000',
		inicioPosterior: '02/05/2024 17:00',
		fim: '',
	},
	{
		id: 'driver-4',
		motorista: 'Marco Alves',
		numMec: '765',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numFrota: '0062',
		telefone: '910000000',
		inicioPosterior: '02/05/2024 17:00',
		fim: '',
	},
]

const defaultVehicles: DriversVehiclesAvailabilityVehicleRow[] = [
	{
		id: 'vehicle-1',
		numFrota: '0062',
		lotacao: '55',
		ano: '2021',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numMec: '199',
		motorista: 'Anabela Ramos',
		telefone: '910000000',
		folga: '',
	},
	{
		id: 'vehicle-2',
		numFrota: '0021',
		lotacao: '40',
		ano: '2021',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numMec: '232',
		motorista: 'Miguel Marques',
		telefone: '910000000',
		folga: '',
	},
	{
		id: 'vehicle-3',
		numFrota: '0129',
		lotacao: '55',
		ano: '2024',
		ultimoServicoInicio: '02/05/2024 15:00',
		ultimoServicoFim: '02/05/2024 16:00',
		origem: 'Faro',
		destino: 'Portim\u00e3o',
		numMec: '564',
		motorista: 'Jorge Santos',
		telefone: '910000000',
		folga: '',
	},
]

const CustomModalDriversVehiclesAvailability: React.FC = () => {
	const { isOpen, closeModal, modalData } = useModal()
	const data = modalData as DriversVehiclesAvailabilityModalData | undefined

	// Set locale to pt-BR
	dayjs.locale('pt-br')

	const initialFilters = {
		startDate: data?.startDate
			? dayjs(data.startDate, 'DD/MM/YYYY HH:mm')
			: dayjs('02/05/2024 13:15', 'DD/MM/YYYY HH:mm'),
		endDate: data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY HH:mm') : dayjs('03/05/2024 16:15', 'DD/MM/YYYY HH:mm'),
		empresa: data?.empresa ?? 'Transol',
		filial: data?.filial ?? 'Transol',
		lotacaoOperador: data?.lotacaoOperador ?? 'Superior ou Igual a',
		lotacao: data?.lotacao ?? '40',
		anoViatura: data?.anoViatura ?? 'Posterior a 2020',
	}
	const [startDate, setStartDate] = useState<Dayjs | null>(initialFilters.startDate)
	const [endDate, setEndDate] = useState<Dayjs | null>(initialFilters.endDate)
	const [empresa, setEmpresa] = useState(initialFilters.empresa)
	const [filial, setFilial] = useState(initialFilters.filial)
	const [lotacaoOperador, setLotacaoOperador] = useState(initialFilters.lotacaoOperador)
	const [lotacao, setLotacao] = useState(initialFilters.lotacao)
	const [anoViatura, setAnoViatura] = useState(initialFilters.anoViatura)
	const [driversDensity, setDriversDensity] = useState<DensityMode>('compact')
	const [vehiclesDensity, setVehiclesDensity] = useState<DensityMode>('compact')

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations(isOpen)

	const driverRows = data?.drivers?.length ? data.drivers : defaultDrivers
	const vehicleRows = data?.vehicles?.length ? data.vehicles : defaultVehicles

	const driverColumns = useMemo<GridColDef<DriversVehiclesAvailabilityDriverRow>[]>(
		() => [
			{
				field: 'motorista',
				headerName: 'Motorista',
				minWidth: 150,
				flex: 1,
				renderCell: params => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Avatar
							sx={{
								borderRadius: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								bgcolor: 'lightgrey 50% / cover no-repeat',
								width: 32,
								height: 32,
								fontSize: '20px',
								fontWeight: 400,
								lineHeight: 1,
							}}
						>
							{String(params.row.motorista ?? 'M').charAt(0)}
						</Avatar>
						<Typography sx={{ fontSize: '12px', lineHeight: 1.3 }}>{params.row.motorista}</Typography>
					</Box>
				),
			},
			{ field: 'numMec', headerName: 'Num Mec', width: 75 },
			{
				field: 'ultimoServicoInicio',
				headerName: '\u00dalt Servi\u00e7o Ini',
				width: 120,
			},
			{
				field: 'ultimoServicoFim',
				headerName: '\u00dalt Servi\u00e7o Fim',
				width: 120,
			},
			{ field: 'origem', headerName: 'Origem', width: 90 },
			{ field: 'destino', headerName: 'Destino', width: 110 },
			{ field: 'numFrota', headerName: 'Num Frota', width: 90 },
			{ field: 'telefone', headerName: 'Telefone', width: 90 },
			{
				field: 'inicioPosterior',
				headerName: 'In\u00edcio Posterior',
				width: 120,
			},
			{ field: 'fim', headerName: 'Fim', width: 70 },
			{
				field: 'actions',
				headerName: '',
				width: 40,
				renderCell: () => (
					<Box className='actions-cell'>
						<IconButton
							size='small'
							sx={{ padding: '4px' }}
						>
							<MoreVertIcon sx={{ fontSize: 18, color: '#00A5FF' }} />
						</IconButton>
					</Box>
				),
			},
		],
		[],
	)

	const vehicleColumns = useMemo<GridColDef<DriversVehiclesAvailabilityVehicleRow>[]>(
		() => [
			{ field: 'numFrota', headerName: 'Num Frota', width: 80 },
			{ field: 'lotacao', headerName: 'Lot', width: 55 },
			{ field: 'ano', headerName: 'Ano', width: 55 },
			{
				field: 'ultimoServicoInicio',
				headerName: '\u00dalt Servi\u00e7o Ini',
				width: 120,
			},
			{
				field: 'ultimoServicoFim',
				headerName: '\u00dalt Servi\u00e7o Fim',
				width: 120,
			},
			{ field: 'origem', headerName: 'Origem', width: 90 },
			{ field: 'destino', headerName: 'Destino', width: 90 },
			{ field: 'numMec', headerName: 'Num Mec', width: 80 },
			{
				field: 'motorista',
				headerName: 'Motorista',
				minWidth: 130,
				flex: 1,
			},
			{ field: 'telefone', headerName: 'Telefone', width: 90 },
			{ field: 'folga', headerName: 'Folga', width: 70 },
			{
				field: 'actions',
				headerName: '',
				width: 40,
				renderCell: () => (
					<Box className='actions-cell'>
						<IconButton
							size='small'
							sx={{ padding: '4px' }}
						>
							<MoreVertIcon sx={{ fontSize: 18, color: '#00A5FF' }} />
						</IconButton>
					</Box>
				),
			},
		],
		[],
	)

	const handleClearFilters = () => {
		setStartDate(initialFilters.startDate)
		setEndDate(initialFilters.endDate)
		setEmpresa(initialFilters.empresa)
		setFilial(initialFilters.filial)
		setLotacaoOperador(initialFilters.lotacaoOperador)
		setLotacao(initialFilters.lotacao)
		setAnoViatura(initialFilters.anoViatura)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Disponibilidade de Motoristas e Viaturas
					</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ padding: '16px 24px 24px', display: 'grid', gap: '24px' }}>
					<Box sx={{ display: 'grid', gap: '16px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
								flexWrap: 'wrap',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '16px',
									flexWrap: 'wrap',
									flex: 1,
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', width: 378 }}>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DateTimePicker
											label='Data Inicial'
											value={startDate}
											onChange={newValue => setStartDate(newValue)}
											format='DD/MM/YYYY HH:mm'
											slots={{ field: ClickableDateTimeField }}
											slotProps={{
												textField: {
													size: 'small',
													sx: [
														inputSx,
														calendarInputSx,
														{
															flex: 1,
															'& .MuiInputAdornment-root': {
																display: 'none',
															},
														},
													],
													InputProps: {
														endAdornment: null,
													},
												},
												...calendarPopperSlotProps,
											}}
											ampm={false}
										/>
										<Typography
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '13px',
												fontWeight: 500,
												color: '#092056',
												width: 13,
												textAlign: 'center',
												alignSelf: 'stretch',
											}}
										>
											{String.fromCharCode(0x2014)}
										</Typography>
										<DateTimePicker
											label='Data Final'
											value={endDate}
											onChange={newValue => setEndDate(newValue)}
											format='DD/MM/YYYY HH:mm'
											slots={{ field: ClickableDateTimeField }}
											slotProps={{
												textField: {
													size: 'small',
													sx: [
														inputSx,
														calendarInputSx,
														{
															flex: 1,
															'& .MuiInputAdornment-root': {
																display: 'none',
															},
														},
													],
													InputProps: {
														endAdornment: null,
													},
												},
												...calendarPopperSlotProps,
											}}
											ampm={false}
										/>
									</LocalizationProvider>
								</Box>

								<TextField
									select
									label='Empresa'
									value={empresa}
									onChange={event => setEmpresa(event.target.value)}
									size='small'
									sx={{ ...inputSx, width: 220 }}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								>
									<MenuItem value='Transol'>Transol</MenuItem>
								</TextField>
								<TextField
									select
									label='Filial'
									value={filial}
									onChange={event => setFilial(event.target.value)}
									size='small'
									sx={{ ...inputSx, width: 220 }}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								>
									<MenuItem value='Transol'>Transol</MenuItem>
								</TextField>
							</Box>
							<CustomButton
								variant='text'
								onClick={handleClearFilters}
								size='small'
								sx={{
									fontSize: '12px',
									fontWeight: 600,
									lineHeight: '22px',
									color: '#00A5FF',
									padding: '4px 0',
									minWidth: 'auto',
									textTransform: 'none',
									'&:hover': { backgroundColor: 'transparent' },
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
							<TextField
								select
								label='Lotação Viatura'
								value={lotacaoOperador}
								onChange={event => setLotacaoOperador(event.target.value)}
								size='small'
								sx={{ ...inputSx, width: 220 }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value='Superior ou Igual a'>Superior ou Igual a</MenuItem>
							</TextField>
							<TextField
								label='Lotação'
								value={lotacao}
								onChange={event => setLotacao(event.target.value)}
								size='small'
								sx={{ ...inputSx, width: 79 }}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								select
								label='Ano Viatura'
								value={anoViatura}
								onChange={event => setAnoViatura(event.target.value)}
								size='small'
								sx={{ ...inputSx, width: 220 }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value='Posterior a 2020'>Posterior a 2020</MenuItem>
							</TextField>
						</Box>
					</Box>

					<Box sx={{ display: 'grid', gap: '24px' }}>
						<Box sx={{ display: 'grid', gap: '16px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
							>
								<Typography
									sx={{
										fontSize: '24px',
										fontWeight: 600,
										lineHeight: 1.334,
										color: '#092056',
									}}
								>
									{driverRows.length} Motoristas com Disponibilidade
								</Typography>
								<ToggleButtonGroup
									value={driversDensity}
									exclusive
									onChange={(_, value) => value && setDriversDensity(value)}
									size='small'
									sx={toggleGroupSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: driversDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espa\u00e7ada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: driversDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={driverRows}
								columns={driverColumns}
								density={driversDensity}
								animateRows={animateRows}
								columnHeaderHeight={40}
								disableRowSelectionOnClick
								pagination={true}
								pageSize={10}
								getRowClassName={params => (params.indexRelativeToCurrentPage === 1 ? 'row--highlight' : '')}
								sx={[tableSx]}
							/>
						</Box>

						<Box sx={{ display: 'grid', gap: '16px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
							>
								<Typography
									sx={{
										fontSize: '24px',
										fontWeight: 600,
										lineHeight: 1.334,
										color: '#092056',
									}}
								>
									{vehicleRows.length} Viaturas com Disponibilidade
								</Typography>
								<ToggleButtonGroup
									value={vehiclesDensity}
									exclusive
									onChange={(_, value) => value && setVehiclesDensity(value)}
									size='small'
									sx={toggleGroupSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: vehiclesDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espa\u00e7ada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: vehiclesDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={vehicleRows}
								columns={vehicleColumns}
								density={vehiclesDensity}
								animateRows={animateRows}
								columnHeaderHeight={40}
								disableRowSelectionOnClick
								pagination={true}
								pageSize={10}
								getRowClassName={params => (params.indexRelativeToCurrentPage === 1 ? 'row--highlight' : '')}
								sx={[tableSx]}
							/>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '8px 24px 16px',
					}}
				>
					<CustomButton
						variant='contained'
						onClick={closeModal}
						sx={popupActionButtonSx}
					>
						Fechar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalDriversVehiclesAvailability
